import {
  useCallback,
  useMemo,
} from 'react';
import type {
  ReactElement,
} from 'react';

import styled from 'styled-components';

import type {
  DropdownMenuItemType,
} from '../createDropdownMenu';

export const StyledAction = styled.button(({
  disabled,

  theme: {
    colors,
    indents,
  },
}) => ({
  padding: 0,
  margin: 0,
  outline: 'none',
  border: 'none',
  background: 'transparent',
  display: 'flex',
  cursor: disabled ? 'default' : 'pointer',
  color: disabled ? colors.NEUTRAL_INPUT : colors.ACTION_DEFAULT,

  '& + &': {
    marginLeft: indents.INDENT_2,
  },

  ':hover': disabled ? undefined : {
    color: colors.ACTION_HOVER,
  },

  ':active': disabled ? undefined : {
    color: colors.ACTION_PRESSED,
  },
}));

export type IconActionProps = {
  action: DropdownMenuItemType;
  disabled: boolean;
};

export function IconAction({
  action: {
    className,
    disabled: isDisabledAction = false,
    label,
    onClick,
    render,
    renderIcon,
  },

  disabled: isDisabledAll,
}: IconActionProps): ReactElement {
  const disabled = isDisabledAll || isDisabledAction;

  const icon = useMemo(() => {
    if (renderIcon) {
      return renderIcon({
        disabled,
      });
    }

    return (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect
          x="0.5"
          y="0.5"
          width="15"
          height="15"
          stroke="currentColor"
        />
      </svg>
    );
  }, [
    disabled,
    renderIcon,
  ]);

  const as = useCallback(({
    className: classNameRenderProp,
  }: {
    className?: string;
  }): ReactElement => {
    if (render) {
      return render({
        children: icon,
        className: classNameRenderProp,
        onClick,
      }) as ReactElement;
    }

    return (
      <button
        type="button"
        className={classNameRenderProp}
        onClick={onClick}
        title={typeof label === 'string' ? label : undefined}
      >
        {icon}
      </button>
    );
  }, [
    icon,
    label,
    onClick,
    render,
  ]);

  if (disabled) {
    return (
      <StyledAction
        type="button"
        disabled
        title={typeof label === 'string' ? label : undefined}
      >
        {icon}
      </StyledAction>
    );
  }

  return (
    <StyledAction
      className={className}
      as={as}
    />
  );
}
